import { useMemo, useState } from "react";
import BarChart from "./BarChart";
import {
  ScrollArea,
  Group,
  Text,
  Pagination,
  Box,
  Paper,
  useMantineTheme,
  Divider,
  Button,
} from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";

import "dayjs/locale/zh-tw";
import {
  INITIAL_ENTITY,
  useChartViewsCountQuery,
  useCategoryEntityQuery,
  useChartEntityQuery,
} from "services/api";

import Th from "features/backstage/_shared/Th";
import useTotalPages from "features/backstage/_shared/useTotalPages";
import Table from "features/backstage/_shared/Table";
import getTodayStartEnd from "utils/getTodayStartEnd";

type ViewsCount = {
  id: number;
  categoryLabel: string;
  chartLabel: string;
  date: string;
};

const PER_PAGE = 20;

const List = () => {
  const theme = useMantineTheme();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<keyof Omit<ViewsCount, "id">>("date");
  const [isDescending, setIsDescending] = useState(false);

  const [duration, setDuration] = useState(getTodayStartEnd());

  const { start, end } = useMemo(() => {
    const [dStart, dEnd] = duration;
    const targetEnd = dEnd && new Date(dEnd.setHours(23, 59, 59, 99));

    return { start: dStart?.toISOString(), end: targetEnd?.toISOString() };
  }, [duration]);

  const { data: chartViewsCountList, isFetching } = useChartViewsCountQuery(
    { start, end },
    { skip: !start || !end }
  );

  const { data: categoryEntity = INITIAL_ENTITY } = useCategoryEntityQuery();
  const { data: chartEntity = INITIAL_ENTITY } = useChartEntityQuery();

  const viewsCountList = useMemo<ViewsCount[]>(() => {
    if (
      !chartViewsCountList ||
      categoryEntity.ids.length === 0 ||
      chartEntity.ids.length === 0
    ) {
      return [];
    }

    return chartViewsCountList
      .map((x) => ({
        id: x.id,
        categoryLabel: categoryEntity.entities[x.mhid]?.label,
        chartLabel: chartEntity.entities[x.bodyId]?.label,
        date: new Date(x.clickDate).toLocaleString(),
      }))
      .sort((x, y) => x[sort].localeCompare(y[sort]) * (isDescending ? -1 : 1));
  }, [chartViewsCountList, categoryEntity, chartEntity, sort, isDescending]);

  const barChart = useMemo(() => {
    if (viewsCountList.length === 0) {
      return { data: [], categories: [] };
    }

    const countEntity = viewsCountList.reduce<{
      ids: string[];
      entities: Record<string, number>;
    }>(
      (acc, curr) => {
        if (!acc.ids.includes(curr.chartLabel)) {
          acc.ids.push(curr.chartLabel);
          acc.entities[curr.chartLabel] = 0;
        }

        acc.entities[curr.chartLabel] += 1;
        return acc;
      },
      { ids: [], entities: {} }
    );

    const sortedList = countEntity.ids.sort(
      (x, y) => countEntity.entities[y] - countEntity.entities[x]
    );

    return {
      data: sortedList.map((x) => countEntity.entities[x]),
      categories: sortedList,
    };
  }, [viewsCountList]);

  const handleSort = (sortBy: keyof Omit<ViewsCount, "id">) => () => {
    setPage(1);

    if (sortBy === sort) {
      setIsDescending((prev) => !prev);
      return;
    }

    setSort(sortBy);
    setIsDescending(false);
  };

  const totalPages = useTotalPages(viewsCountList.length);
  const skip = (page - 1) * PER_PAGE;

  const handleExport = () => {
    const csvContent =
      "data:text/csv;charset=utf-8,ID,MHID,BODYID,MHNAME,BOARDNAME,CLICKDATE,FROMIP\n" +
      chartViewsCountList
        .map(
          (x) =>
            `${x.id},${x.mhid},${x.bodyId},${x.mhname ?? ""},${
              x.boardName ?? ""
            },${x.clickDate},${x.fromIp}`
        )
        .join("\n");

    console.log({ csvContent });

    var encodedUri = encodeURI(csvContent);
    var link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "儀表板瀏覽統計.csv");
    document.body.appendChild(link); // Required for FF

    link.click();
  };

  const rows = viewsCountList.slice(skip, skip + PER_PAGE).map((row) => (
    <tr key={row.id}>
      <td>{row.categoryLabel}</td>
      <td>{row.chartLabel}</td>
      <td>{row.date}</td>
    </tr>
  ));

  return (
    <Box py={8} px={36}>
      <Group position="apart">
        <h2>儀表板瀏覽統計</h2>
      </Group>

      <Group mb={16}>
        <DateRangePicker
          locale="zh-tw"
          placeholder="選擇開始與結束時間"
          value={duration}
          disabled={isFetching}
          onChange={(values) => {
            setPage(1);
            setDuration(values);
          }}
          style={{
            flex: "30% 0 0",
          }}
          withinPortal
        />

        <Button color="cyan" onClick={handleExport}>
          匯出
        </Button>
      </Group>

      <Divider label="儀表板瀏覽統計圖表"></Divider>

      <Paper
        px={16}
        pt={4}
        pb={8}
        mt={16}
        mb={40}
        radius={"lg"}
        shadow={theme.shadows.md}
      >
        <BarChart data={barChart.data} categories={barChart.categories} />
      </Paper>

      <Divider label="儀表板瀏覽明細資料"></Divider>

      <Paper
        px={16}
        pt={4}
        pb={8}
        mt={16}
        mb={40}
        radius={"lg"}
        shadow={theme.shadows.lg}
      >
        <ScrollArea>
          <Table>
            <thead>
              <tr>
                <Th
                  sorted={sort === "categoryLabel"}
                  reversed={isDescending}
                  onSort={handleSort("categoryLabel")}
                  cellWidth="40%"
                >
                  分類名稱
                </Th>
                <Th
                  sorted={sort === "chartLabel"}
                  reversed={isDescending}
                  onSort={handleSort("chartLabel")}
                  cellWidth="40%"
                >
                  儀表板名稱
                </Th>
                <Th
                  sorted={sort === "date"}
                  reversed={isDescending}
                  onSort={handleSort("date")}
                  cellWidth="40%"
                >
                  點擊時間
                </Th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows
              ) : (
                <tr>
                  <td colSpan={3}>
                    <Text weight={500} align="center">
                      沒有找到相關資料
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>

        <Pagination
          pt={24}
          pb={16}
          total={totalPages}
          page={page}
          color="cyan"
          onChange={setPage}
        />
      </Paper>
    </Box>
  );
};
export default List;
